import { FormControlProps, OutlinedInput, OutlinedInputProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import { RateType as RateTypeEnum } from "@sinch/core/entities/serverEnums";
import { UnitName } from "@sinch/core/entities/serverEnums.types";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import type { RateType } from "@sinch/types/sinch.types";
import { isNotNil } from "ramda-adjunct";
import React, { FC, useCallback } from "react";
import type { FieldPath } from "react-hook-form";
import { get, useFormState } from "react-hook-form";
import { FormControlWrapper, InputWrapperProps } from "@sinch/components";
import { RateIntervals } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/queries.graphql";
import { RateIntervalsQuery } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/queries.types";
import { RateInputInputs } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/RateInput/RateInputInputs";
import { RateBuilderTreeFormFields } from "@sinch/components/application/RatelistBuilder/ratelistBuilderSchema";
import { InputBaseTransferredProps } from "@sinch/components/form/CombinedInput/CombinedInput";
import { InputSkeleton } from "@sinch/components/form/InputSkeleton/InputSkeleton";

export type RateInputProps = Omit<OutlinedInputProps, "value" | "onChange" | "name"> &
  InputBaseTransferredProps &
  Omit<InputWrapperProps, "slots" | "slotProps"> & {
    FormControlProps?: FormControlProps;
    size?: OutlinedInputProps["size"];
    value: RateInputValue;
    onChange: (value: RateInputValue) => void;
    unit?: string | null;
    rateName?: string | null;
    name: FieldPath<RateBuilderTreeFormFields>;
    unitName: UnitName | null;
    availableRateComponents?: string[];
  };

export interface RateInputValue {
  baseComponent: string | null;
  amount: number | null;
  type: RateType | RateTypeEnum;
}

export const RateInput: FC<RateInputProps> = ({
  label,
  FormHelperTextProps: formHelperTextProps,
  FormControlProps: formControlProps,
  helperText,
  required,
  infoTooltip,
  fullWidth,
  size,
  onChange,
  unit = null,
  rateName = null,
  name,
  errorMessage,
  unitName,
  availableRateComponents,
  ...props
}) => {
  // TODO: improve loading
  const { loading } = useQueryHandler<RateIntervalsQuery>(RateIntervals);

  const inputRefs = React.useRef<(HTMLInputElement | null)[]>([null, null, null]);
  const [focusedIndex, setFocusedIndex] = React.useState(-1);
  const { errors } = useFormState<RateBuilderTreeFormFields>();
  const amountError = get(errors, `${name}.amount`);
  const baseComponentError = get(errors, `${name}.baseComponent`);
  const errorMessages = [amountError?.message, baseComponentError?.message, ...(errorMessage ?? [])].filter((element) =>
    isNotNil(element)
  );

  const handleValueChange = useCallback(
    (e: RateInputValue) => {
      onChange(e);
    },
    [onChange]
  );

  return (
    <InputSkeleton active={loading}>
      <FormControlWrapper
        error={amountError || baseComponentError}
        errorMessage={errorMessages}
        focused={focusedIndex > -1}
        FormHelperTextProps={formHelperTextProps}
        helperText={helperText}
        infoTooltip={infoTooltip}
        label={label}
        required={required}
        {...formControlProps}
        fullWidth={fullWidth}
      >
        <RateInputOutlined
          fullWidth={fullWidth}
          required={required}
          size={size}
          slotProps={{
            // @ts-ignore hard to override
            input: {
              onChangeValue: handleValueChange,
              onFocusChange: setFocusedIndex,
              inputRefs: inputRefs,
              unit: unit,
              rateName: rateName,
              unitName: unitName,
              availableRateComponents,
            },
          }}
          slots={{ input: RateInputInputs }}
          {...props}
          error={amountError || baseComponentError}
        />
      </FormControlWrapper>
    </InputSkeleton>
  );
};

const RateInputOutlined = styled(OutlinedInput, {
  shouldForwardProp: (prop) => prop !== "size",
})<{ size?: OutlinedInputProps["size"] }>(({ theme, size }) => ({
  height: size === "small" ? theme.shape.inputSmallHeight : theme.shape.inputMediumHeight,
}));
